import React from "react"
import { Card, Typography, Radio, Input, Space } from "antd"
import styles from "../index.module.css"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { FEE_PAYER_MODE, SPLIT_TYPE } from "../hooks/usePlatformFees"

const { Title, Text } = Typography

const PlatformFeesCard = ({
  feePayerMode,
  setFeePayerMode,
  splitType,
  setSplitType,
  guidePortion,
  setGuidePortion,
  guidePortionError,
  guestPortion,
  isLoading
}) => {
  const { t } = useTranslation()

  const splitPortionInput = (
    <div style={{ marginLeft: 24, marginTop: 12 }}>
      <Radio.Group
        onChange={(e) => setSplitType(e.target.value)}
        value={splitType}
        style={{ display: "flex", gap: 16, marginBottom: 16 }}
        disabled={isLoading}
      >
        <Radio value={SPLIT_TYPE.PERCENTAGE}>{t("settings.split_by_percentage")}</Radio>
        <Radio value={SPLIT_TYPE.FIXED}>{t("settings.split_by_fixed_amount")}</Radio>
      </Radio.Group>

      <Space direction="vertical" size={4}>
        <Space align="center" size="middle">
          <Text>{t("settings.you_pay")}</Text>

          {splitType === SPLIT_TYPE.PERCENTAGE ? (
            <>
              <Input
                type="number"
                value={guidePortion}
                onChange={(e) => setGuidePortion(e.target.value)}
                placeholder="50"
                style={{ width: 100 }}
                size="large"
                min={0}
                step={1}
                status={guidePortionError ? "error" : ""}
                disabled={isLoading}
              />
              <Text>%</Text>
              <Text type="secondary">
                {t("settings.guest_pays_label")}: {guestPortion ?? "--"}%
              </Text>
            </>
          ) : (
            <>
              <Input
                type="number"
                prefix="$"
                value={guidePortion}
                onChange={(e) => setGuidePortion(e.target.value)}
                placeholder="0.00"
                style={{ width: 130 }}
                size="large"
                min={0}
                step={0.01}
                status={guidePortionError ? "error" : ""}
                disabled={isLoading}
              />
              <Text type="secondary">{t("settings.guest_pays_rest")}</Text>
            </>
          )}
        </Space>
        {guidePortionError && <Text type="danger" style={{ fontSize: 12 }}>{guidePortionError}</Text>}
      </Space>
    </div>
  )

  return (
    <Card className={styles.cardContainer}>
      <div className={styles.headerRow}>
        <div className={styles.titleWithInfo}>
          <Title level={4}>{t("settings.platform_fees")}</Title>
        </div>
      </div>
      <Text className={styles.description}>{t("settings.platform_fees_tooltip")}</Text>

      <Radio.Group
        className={styles.platformRadioGroup}
        onChange={(e) => setFeePayerMode(e.target.value)}
        value={feePayerMode}
        disabled={isLoading}
      >
        <div className={styles.platformRadioOption}>
          <Radio value={FEE_PAYER_MODE.GUEST_PAYS}>
            <span className={styles.platformRadioLabel}>{t("settings.guest_pays_ds_fee")}</span>
            <div className={styles.platformRadioDesc}>{t("settings.guest_pays_ds_fee_desc")}</div>
          </Radio>
        </div>

        <div className={styles.platformRadioOption}>
          <Radio value={FEE_PAYER_MODE.GUIDE_PAYS}>
            <span className={styles.platformRadioLabel}>{t("settings.guide_or_business_pays_ds_fee")}</span>
            <div className={styles.platformRadioDesc}>{t("settings.guide_or_business_pays_ds_fee_desc")}</div>
          </Radio>
        </div>

        <div className={styles.platformRadioOption}>
          <Radio value={FEE_PAYER_MODE.SPLIT}>
            <span className={styles.platformRadioLabel}>{t("settings.split_ds_fee")}</span>
            <div className={styles.platformRadioDesc}>{t("settings.split_ds_fee_desc")}</div>
          </Radio>
          {feePayerMode === FEE_PAYER_MODE.SPLIT && splitPortionInput}
        </div>
      </Radio.Group>
    </Card>
  )
}

PlatformFeesCard.propTypes = {
  feePayerMode: PropTypes.string.isRequired,
  setFeePayerMode: PropTypes.func.isRequired,
  splitType: PropTypes.string.isRequired,
  setSplitType: PropTypes.func.isRequired,
  guidePortion: PropTypes.string.isRequired,
  setGuidePortion: PropTypes.func.isRequired,
  guidePortionError: PropTypes.string,
  guestPortion: PropTypes.number,
  isLoading: PropTypes.bool
}

export default PlatformFeesCard
